<template>
  <div class="row">
    <div class="col-lg-12">
      <h2 class="breadcrumbs">
        <o-tree-breadcrumbs
          :value="category_id"
          :items="optionDocumentCategoryItems"
        />
        <template v-if="!category_id">
          <h2>Library</h2>
        </template>
      </h2>

      <div class="row mb-4">
        <div class="col-lg-12">
          <div class="search-container">
            <div class="input-group input-group-lg">
              <span class="input-group-text bg-primary text-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                  />
                </svg>
              </span>
              <input
                type="search"
                class="form-control form-control-lg"
                placeholder="Search documents in library... (Press Enter to search)"
                v-model="searchText"
                @input="onQueryChange"
                @keyup.enter="onEnter"
              />
              <button
                v-if="searchText"
                class="btn btn-outline-secondary"
                type="button"
                @click="clearSearch"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="searching">
        <h4>Searching: {{ list_options.q }}</h4>
      </div>

      <library-folder
        v-if="current_category"
        description="Back Up"
        :category="parent_category_id"
        up
        :list-options="query_list_options"
      ></library-folder>

      <library-folder
        v-for="subsection in sub_categories"
        :key="subsection.id"
        :description="subsection.description"
        :category="subsection.id"
        :list-options="query_list_options"
      ></library-folder>

      <b-alert
        class="my-3"
        show
        variant="info"
        v-if="!custom_items || custom_items.length === 0"
      >
        <h4>Sorry no documents found</h4>
      </b-alert>

      <LibraryDocument
        v-for="document in custom_items"
        :key="document.id"
        :q="list_options.q"
        :document="document"
      />

      <o-list-paging
        :limit="query_list_options.limit"
        :offset="query_list_options.offset"
        :total-items="total_items"
        @change-limit="change_limit"
        @change-page="change_page"
      />
    </div>
  </div>
</template>

<script>
import {
  CreateGroupPermissionMixin,
  CreateListPageMixin,
} from "../../../vue-ocom/mixins";
import FetchAndUseCodeModMixin from "../../../vue-ocom/mixins/FetchAndUseCodeModMixin";
import OTreeBreadcrumbs from "../../../vue-ocom/components/tree/OTreeBreadcrumbs.vue";
import OTree from "../../../vue-ocom/optional/tree/OTree.vue";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import LibraryFolder from "@/vue-dms/library/pages/LibraryFolder.vue";
import LibraryDocument from "@/vue-dms/library/pages/LibraryDocument.vue";
import _, { cloneDeep, sortBy, reverse, isEqual, filter } from "lodash";
import { filter_active_list } from "@/vue-ocom/utils/active.js";

export default {
  name: "Library",
  components: {
    LibraryFolder,
    LibraryDocument,
    OTreeBreadcrumbs,
    OTree,
    OListPaging,
  },
  mixins: [
    CreateListPageMixin("Document", "document", "library"),
    CreateGroupPermissionMixin("Document"),
    FetchAndUseCodeModMixin(["CodeDocumentType", "DocumentCategory"]),
  ],
  data() {
    return {
      searchText: "",
      searching: false,
    };
  },
  methods: {
    before_get_all(list_options) {
      /* Overwrite this to update the list_options before the data is changed
       * NOT: use _.cloneDeep(list_options) so the url is not changed
       * */
      var result = cloneDeep(list_options);
      result.filter = "library";
      result.category = this.selected_categories;
      return result;
    },

    onEnter() {
      this.list_options.q = this.searchText.trim();

      this.searching = true;
      if (!this.searchText.trim()) {
        this.searching = false;
      }
      this.update_table(this.list_options);
    },
    onQueryChange(event) {
      if (event.target.value.trim().length === 0) {
        this.onEnter();
      }
    },
    clearSearch() {
      this.searchText = "";
      this.searching = false;
      this.list_options.q = "";
      this.update_table(this.list_options);
    },
    get_document_title(doc) {
      if (doc.current_version) {
        return doc.current_version.name;
      } else {
        if (doc.current_draft) {
          return doc.current_draft.name;
        }
      }

      return "Unknown document";
    },
    update_category(newCategory) {
      var ids = _.map(newCategory, function (c) {
        return c.toString();
      });

      this.$router.push({
        name: "library.category",
        params: {
          category: ids,
        },
      });
    },
  },
  computed: {
    category_id() {
      // the ID of the category they are on
      var cat = this.$route.params.category;
      var result = Number.parseInt(cat);
      if (Number.isNaN(result)) {
        return null;
      }
      return result;
    },
    current_category() {
      // The Current Category Item
      if (this.category_id) {
        return this.optionDocumentCategoryItemsById[this.category_id];
      }
    },
    parent_category_id() {
      // the ID of the parent
      if (this.current_category) {
        return _.last(this.current_category.parents);
      }

      return 0; // HOME
    },
    selected_categories() {
      if (this.current_category) {
        return [...this.current_category.parents, this.current_category.id];
      }
      return []; // empty
    },
    sub_categories() {
      // Get the Categories where the selected is the parent

      var selectedCategories = this.selected_categories;

      return _.filter(this.active_categories, function (category) {
        if (_.isEqual(category.parents, selectedCategories)) {
          return category;
        }
      });
    },
    active_categories() {
      return filter_active_list(this.optionDocumentCategoryItems);
    },
    custom_items() {
      const vm = this;
      var result = this.items;

      // Only filter by category if not searching
      if (this.selected_categories && !this.searching) {
        var selected_categories = this.selected_categories;

        result = filter(result, function (item) {
          return isEqual(item.doc_category, selected_categories);
        });
      }

      result = reverse(
        sortBy(result, function (doc) {
          return vm.get_document_title(doc);
        })
      );

      return result;
    },
  },
  watch: {
    "list_options.q": function (newVal) {
      // Sync searchText when list_options.q changes (e.g., from URL)
      if (newVal && newVal !== this.searchText) {
        this.searchText = newVal;
        this.searching = true;
      } else if (!newVal && this.searchText) {
        this.searchText = "";
        this.searching = false;
      }
    },
  },
  created() {
    // Initialize searchText from list_options.q if it exists
    if (this.list_options && this.list_options.q) {
      this.searchText = this.list_options.q;
      this.searching = true;
    }
  },
};
</script>

<style scoped>
.navigationPane {
  background: #f9f9f9;
  border-right: #cccccc 1px solid;
  min-height: 1024px;
}
.breadcrumbs {
  background: #e7eef7;
  border: 1px silver solid;
  padding: 5px;
}
.search-container {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.search-container .input-group-text {
  border: none;
}
.search-container .form-control {
  border: 2px solid #dee2e6;
  transition: border-color 0.2s;
}
.search-container .form-control:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
}
</style>
